import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import {
  Phone,
  Mail,
  MapPin,
  ShieldCheck,
  Clock,
  Award,
  CheckCircle2,
  ArrowRight,
  Home,
  Hammer,
  Paintbrush,
  Thermometer,
  Star,
  ChevronRight,
  Mountain,
  Droplets,
  AlertTriangle,
} from "lucide-react";
import { BUSINESS } from "../data/regions";

// Icon mapping for services
const ServiceIcons = [Home, Hammer, Paintbrush, Thermometer, Droplets];

const CityPage = ({ cityData, region }) => {
  // Update SEO meta tags
  useEffect(() => {
    document.title = cityData.seo.title;
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement("meta");
      metaDescription.name = "description";
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute("content", cityData.seo.description);

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement("meta");
      metaKeywords.name = "keywords";
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute("content", cityData.seo.keywords);

    window.scrollTo(0, 0);
  }, [cityData]);

  const isAlpine = cityData.region === "haute-savoie";

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Hero Section */}
      <section className="relative bg-[#0F172A] text-white py-32 lg:py-40">
        <div className="absolute inset-0 opacity-20">
          <div className="w-full h-full bg-gradient-to-br from-[#C2410C]/30 to-transparent" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
          {/* Breadcrumb */}
          <nav className="mb-8">
            <ol className="flex items-center gap-2 text-sm text-white/60">
              <li>
                <Link to={isAlpine ? "/haute-savoie" : "/"} className="hover:text-white transition-colors">
                  {isAlpine ? "Haute-Savoie" : "Yvelines"}
                </Link>
              </li>
              <li>/</li>
              <li className="text-white">{cityData.city}</li>
            </ol>
          </nav>

          <Badge className="mb-6 bg-[#C2410C] text-white border-none uppercase tracking-wider text-xs px-4 py-2">
            {isAlpine ? "Spécialiste climat alpin" : "Artisan local"} • Garantie décennale
          </Badge>

          <h1 
            className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-tight mb-6 font-['Barlow_Condensed']"
            data-testid="city-page-h1"
          >
            {cityData.h1}
          </h1>

          <p className="text-xl text-white/80 max-w-3xl mb-8">
            Artisan couvreur, charpentier et façadier à {cityData.city}. 
            Devis gratuit sous 24h. Intervention rapide {cityData.department}.
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <a href="#contact-city">
              <Button className="bg-[#C2410C] hover:bg-[#9A3412] h-14 px-8 text-base uppercase font-bold tracking-widest">
                Recevoir mon devis gratuit
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
            <a href={BUSINESS.phoneLink}>
              <Button variant="outline" className="h-14 px-8 text-base uppercase font-bold tracking-widest text-white border-white hover:bg-white hover:text-[#0F172A]">
                <Phone className="mr-2 w-5 h-5" />
                {BUSINESS.phone}
              </Button>
            </a>
          </div>

          {/* Trust badges */}
          <div className="mt-12 flex flex-wrap gap-6">
            <div className="flex items-center gap-2 text-white/80">
              <ShieldCheck className="w-5 h-5 text-[#C2410C]" />
              <span className="text-sm">Garantie décennale</span>
            </div>
            <div className="flex items-center gap-2 text-white/80">
              <Clock className="w-5 h-5 text-[#C2410C]" />
              <span className="text-sm">Devis sous 24h</span>
            </div>
            <div className="flex items-center gap-2 text-white/80">
              <Award className="w-5 h-5 text-[#C2410C]" />
              <span className="text-sm">Intervention rapide</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content Column */}
            <div className="lg:col-span-2 space-y-12">
              {/* Introduction */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-3xl font-bold uppercase tracking-tight font-['Barlow_Condensed'] text-[#0F172A] mb-6">
                  Votre couvreur de confiance à {cityData.city}
                </h2>
                <div 
                  className="text-[#64748B] leading-relaxed space-y-4"
                  dangerouslySetInnerHTML={{ __html: cityData.intro.split('\n\n').map(p => `<p>${p}</p>`).join('') }}
                />
              </div>

              {/* Services Section */}
              <div>
                <h2 className="text-3xl font-bold uppercase tracking-tight font-['Barlow_Condensed'] text-[#0F172A] mb-8">
                  Nos services à {cityData.city}
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  {cityData.services.map((service, index) => {
                    const Icon = ServiceIcons[index % ServiceIcons.length];
                    return (
                      <Card key={index} className="border-none shadow-md hover:shadow-lg transition-shadow">
                        <CardContent className="p-6">
                          <div className="w-12 h-12 bg-[#F1F5F9] flex items-center justify-center mb-4">
                            <Icon className="w-6 h-6 text-[#C2410C]" />
                          </div>
                          <h3 className="font-bold text-lg text-[#0F172A] mb-3 font-['Barlow_Condensed'] uppercase tracking-tight">
                            {service.title}
                          </h3>
                          <p className="text-[#64748B] text-sm leading-relaxed">
                            {service.description}
                          </p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>

              {/* Why Choose Us */}
              <div className="bg-white p-8 shadow-md">
                <h2 className="text-3xl font-bold uppercase tracking-tight font-['Barlow_Condensed'] text-[#0F172A] mb-6">
                  Pourquoi choisir BKR Toiture à {cityData.city} ?
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { icon: MapPin, text: `Artisan local intervenant à ${cityData.city}` },
                    { icon: ShieldCheck, text: "Garantie décennale sur tous les travaux" },
                    { icon: Clock, text: "Devis gratuit sous 24 heures" },
                    { icon: Award, text: "Plus de 10 ans d'expérience" },
                    { icon: CheckCircle2, text: "Matériaux de qualité professionnelle" },
                    { icon: Star, text: "Clients satisfaits dans tout le département" },
                    { icon: Droplets, text: "Recherche de fuites et dépannage urgent" },
                    { icon: AlertTriangle, text: "Intervention après tempête et sinistre" },
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <item.icon className="w-5 h-5 text-[#C2410C] flex-shrink-0 mt-0.5" />
                      <span className="text-[#0F172A] text-sm">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Problèmes courants de toiture - SEO Section */}
              {cityData.commonProblems && cityData.commonProblems.length > 0 && (
                <div>
                  <h2 className="text-3xl font-bold uppercase tracking-tight font-['Barlow_Condensed'] text-[#0F172A] mb-8">
                    Problèmes de toiture fréquents à {cityData.city}
                  </h2>
                  <div className="space-y-6">
                    {cityData.commonProblems.map((problem, index) => (
                      <div key={index} className="bg-white p-6 shadow-sm border-l-4 border-[#C2410C]">
                        <h3 className="font-bold text-lg text-[#0F172A] mb-3 font-['Barlow_Condensed'] uppercase tracking-tight">
                          {problem.title}
                        </h3>
                        <div
                          className="text-[#64748B] text-sm leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: problem.text }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Local Intervention */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-3xl font-bold uppercase tracking-tight font-['Barlow_Condensed'] text-[#0F172A] mb-6">
                  Zone d'intervention autour de {cityData.city}
                </h2>
                <div 
                  className="text-[#64748B] leading-relaxed space-y-4"
                  dangerouslySetInnerHTML={{ __html: cityData.localParagraph.split('\n\n').map(p => `<p>${p}</p>`).join('') }}
                />
                
                {/* Nearby Areas */}
                <div className="mt-6 flex flex-wrap gap-2">
                  {cityData.nearbyAreas.map((area, index) => (
                    <Badge key={index} variant="secondary" className="bg-[#F1F5F9] text-[#64748B]">
                      {area}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* CTA Section */}
              <div className="bg-[#0F172A] p-8 text-white">
                <h2 className="text-2xl font-bold uppercase tracking-tight font-['Barlow_Condensed'] mb-4">
                  Besoin d'un couvreur à {cityData.city} ?
                </h2>
                <p className="text-white/80 mb-6">
                  Contactez-nous dès maintenant pour un devis gratuit et personnalisé. 
                  Intervention rapide garantie dans tout le secteur de {cityData.city}.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a href={BUSINESS.phoneLink}>
                    <Button className="bg-[#C2410C] hover:bg-[#9A3412] h-12 px-6 uppercase font-bold tracking-wider">
                      <Phone className="mr-2 w-4 h-4" />
                      Appeler maintenant
                    </Button>
                  </a>
                  <a href="#contact-city">
                    <Button variant="outline" className="h-12 px-6 uppercase font-bold tracking-wider text-white border-white hover:bg-white hover:text-[#0F172A]">
                      Demander un devis
                    </Button>
                  </a>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Contact Card */}
              <div id="contact-city" className="bg-white p-6 shadow-lg sticky top-24">
                <h3 className="text-xl font-bold uppercase tracking-tight font-['Barlow_Condensed'] text-[#0F172A] mb-4">
                  Contact rapide
                </h3>
                <p className="text-[#64748B] text-sm mb-6">
                  Réponse garantie sous 24h. Devis gratuit et sans engagement.
                </p>
                
                <div className="space-y-4 mb-6">
                  <a
                    href={BUSINESS.phoneLink}
                    className="flex items-center gap-3 p-4 bg-[#C2410C] text-white hover:bg-[#9A3412] transition-colors"
                  >
                    <Phone className="w-5 h-5" />
                    <div>
                      <p className="text-xs uppercase tracking-wider opacity-80">Téléphone</p>
                      <p className="font-bold">{BUSINESS.phone}</p>
                    </div>
                  </a>
                  
                  <a
                    href={`mailto:${BUSINESS.email}`}
                    className="flex items-center gap-3 p-4 bg-[#F1F5F9] text-[#0F172A] hover:bg-[#E2E8F0] transition-colors"
                  >
                    <Mail className="w-5 h-5 text-[#C2410C]" />
                    <div>
                      <p className="text-xs uppercase tracking-wider text-[#64748B]">Email</p>
                      <p className="font-medium text-sm">{BUSINESS.email}</p>
                    </div>
                  </a>
                </div>

                {/* Quick Form CTA */}
                <Link to={isAlpine ? "/haute-savoie#contact" : "/#contact"}>
                  <Button className="w-full bg-[#0F172A] hover:bg-[#1E293B] h-12 uppercase font-bold tracking-wider">
                    Recevoir mon devis gratuit
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>

              {/* Guarantees Card */}
              <div className="bg-[#0F172A] p-6 text-white">
                <h3 className="text-lg font-bold uppercase tracking-tight font-['Barlow_Condensed'] mb-4">
                  Nos garanties
                </h3>
                <ul className="space-y-3">
                  {[
                    "Garantie décennale",
                    "Devis gratuit sous 24h",
                    "Intervention rapide",
                    "Matériaux professionnels",
                    "Artisan local qualifié",
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#C2410C]" />
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Other Cities */}
              <div className="bg-white p-6 shadow-md">
                <h3 className="text-lg font-bold uppercase tracking-tight font-['Barlow_Condensed'] text-[#0F172A] mb-4">
                  Autres villes {cityData.department}
                </h3>
                <ul className="space-y-2">
                  {cityData.nearbyAreas.slice(0, 5).map((area, index) => (
                    <li key={index} className="flex items-center gap-2 text-[#64748B] hover:text-[#C2410C] transition-colors">
                      <ChevronRight className="w-4 h-4" />
                      <span className="text-sm">{area}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#C2410C] py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16 text-center">
          <h2 className="text-3xl font-bold uppercase tracking-tight font-['Barlow_Condensed'] text-white mb-4">
            Couvreur {cityData.city} – Devis gratuit
          </h2>
          <p className="text-white/90 mb-8 max-w-2xl mx-auto">
            Appelez-nous maintenant ou demandez votre devis en ligne. 
            Réponse garantie sous 24 heures, intervention rapide sur {cityData.city} et environs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={BUSINESS.phoneLink}>
              <Button className="bg-white text-[#C2410C] hover:bg-[#F8FAFC] h-14 px-8 text-base uppercase font-bold tracking-widest">
                <Phone className="mr-2 w-5 h-5" />
                {BUSINESS.phone}
              </Button>
            </a>
            <Link to={isAlpine ? "/haute-savoie#contact" : "/#contact"}>
              <Button variant="outline" className="h-14 px-8 text-base uppercase font-bold tracking-widest text-white border-white hover:bg-white hover:text-[#C2410C]">
                Formulaire de devis
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CityPage;
